/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/* ============================================================================
 * Devoteewear CRO 2026-09-13 — PDP colour gallery, v2.3 (replaces the 2026-09-09 block).
 *
 * WHAT v1 GOT WRONG (measured 2026-09-13, 134 products, phone + desktop):
 *  - PHONES: Focal's own `_onVariantChanged` already re-filters the photo carousel
 *    correctly (destroys Flickity, toggles `.is-filtered`, rebuilds it with
 *    `cellSelector: .product__media-item:not(.is-filtered)`). v1 re-toggled `.is-filtered`
 *    50 ms later with different matching, after Flickity was rebuilt, so photos fell out of
 *    the carousel: the photo area ballooned to 900-2,600 px or collapsed and pushed
 *    Size / Add to Cart off-screen (11 products, e.g. the Confidence range).
 *  - DESKTOP: the grid gallery (`.product-fancybox`) hides the carousel, and Focal's handler
 *    waits on that hidden carousel forever, so a colour click changed nothing. v1 fixed
 *    colour-tagged photos only, and read tags from the DOM (videos have no alt, so missed).
 *  - ON LOAD (pre-existing, phones + desktop): 6 products open with one colour selected
 *    but another colour's photo on screen (their first photo is not the default variant's).
 *
 * v2.1 RULES:
 *  1. Phones/tablets (carousel visible): colour taps are left to Focal (native is correct).
 *  2. Desktop (grid visible), on each variant change of the main product form:
 *     a. Colour-tagged photos (`#color_<value>`): show only that colour's photos plus
 *        untagged shared shots, and put the variant's own photo first.
 *     b. No tagged photo for the colour: hide nothing, put the variant's own photo first.
 *     Tags are read from the product data (Focal's `product-media.product`), then the DOM.
 *  3. On page load (both layouts): only if the photo on screen does not show the selected
 *     colour, show the selected variant's own photo (phones: instant jump through Focal's
 *     own carousel; desktop: rule 2). Otherwise do nothing.
 *  4. Fail-safe: when nothing can be matched, touch nothing.
 *
 * v2.2 (critique hardening, same behaviour on product pages): an event from a form that isn't
 *  the page's gallery never touches the main gallery; the on-load phone check reads the slide
 *  Flickity actually shows (not a possibly stale class); promise errors are swallowed;
 *  single-photo products exit at once.
 * v2.3: Focal's event detail.formId is `form.id`, and the product form has an input named "id",
 *  so detail.formId is that <input>, not the form's id string. v2.3 reads the id attribute of the
 *  form that dispatched the event (event.target) instead. (v2.2 lost desktop taps to this.)
 *
 * ROLLBACK: delete this block (or restore the 2026-09-09 block).
 * ==========================================================================*/
(function () {
  var norm = function (s) {
    return String(s || '').replace(/&amp;/g, '&').toLowerCase()
      .replace(/^#\s*colou?r[\s_-]*/, '').replace(/[\s_-]+/g, '').trim();
  };
  var isTag = function (alt) { return /^#\s*colou?r/i.test(String(alt || '').trim()); };
  var toArr = function (list) { return Array.prototype.slice.call(list || []); };

  function mainMedia(formId) {
    if (formId) return document.querySelector('product-media[form-id="' + formId + '"]');
    return document.querySelector('product-media[form-id^="product-form-main"]') || document.querySelector('product-media');
  }
  function carousel(media) { return media.querySelector('flickity-carousel.product__media-list'); }
  function carouselVisible(media) {
    var list = carousel(media);
    if (!list) return false;
    var wrap = list.parentElement || list;
    return getComputedStyle(wrap).display !== 'none' && wrap.getBoundingClientRect().width > 0;
  }
  function altOf(media, item) {
    var id = item.getAttribute('data-media-id'), p = media.product;
    if (p && p.media) {
      for (var i = 0; i < p.media.length; i++) if (String(p.media[i].id) === id) return p.media[i].alt || '';
    }
    var im = item.querySelector('img[alt]');
    return im ? (im.getAttribute('alt') || '') : '';
  }
  function colourValue(media, variant) {
    var p = media.product;
    if (p && p.options && variant && variant.options) {
      for (var i = 0; i < p.options.length; i++) {
        var o = p.options[i], name = typeof o === 'string' ? o : (o && o.name);
        if (/colou?r|shade/i.test(name || '')) return norm(variant.options[i]);
      }
    }
    var alt = variant && variant.featured_media && variant.featured_media.alt;
    return isTag(alt) ? norm(alt) : null;
  }
  function showsColour(media, item, variant, want) {
    if (!item) return false;
    if (variant.featured_media && item.getAttribute('data-media-id') === String(variant.featured_media.id)) return true;
    var a = altOf(media, item);
    return !!want && isTag(a) && norm(a) === want;
  }
  function gridItems(media) {
    var grid = media.querySelector('.product-fancybox__wrapper');
    return grid ? toArr(grid.querySelectorAll('.product__media-item')) : [];
  }
  function firstVisible(items) {
    var vis = items.filter(function (n) {
      var q = n.getBoundingClientRect();
      return !n.classList.contains('is-filtered') && q.width > 0 && q.height > 0;
    });
    vis.sort(function (a, b) {
      var qa = a.getBoundingClientRect(), qb = b.getBoundingClientRect();
      return (qa.top - qb.top) || (qa.left - qb.left);
    });
    return vis[0] || null;
  }

  function refilterGrid(media, variant) {
    var items = gridItems(media);
    if (!items.length) return;
    var want = colourValue(media, variant);
    var featuredId = variant && variant.featured_media ? String(variant.featured_media.id) : null;
    var matched = want ? items.filter(function (n) { var a = altOf(media, n); return isTag(a) && norm(a) === want; }) : [];
    var target = null, k;

    if (matched.length) {
      // Rule 2a: only the chosen colour's tagged photos (untagged shared shots stay).
      for (k = 0; k < matched.length; k++) {
        if (matched[k].getAttribute('data-media-id') === featuredId) { target = matched[k]; break; }
      }
      target = target || matched[0];
      items.forEach(function (n) { var a = altOf(media, n); n.classList.toggle('is-filtered', isTag(a) && norm(a) !== want); });
    } else {
      // Rule 2b: no tagged photo for this colour; hide nothing, variant's own photo first.
      if (!featuredId) return; // fail-safe
      target = items.filter(function (n) { return n.getAttribute('data-media-id') === featuredId; })[0] || null;
      if (!target) return;     // fail-safe
      items.forEach(function (n) { n.classList.remove('is-filtered'); });
    }
    items.forEach(function (n) { n.style.order = ''; n.classList.toggle('is-selected', n === target); });
    target.style.order = '-1';
  }

  function currentVariant(media) {
    var form = document.getElementById(media.getAttribute('form-id'));
    var input = form && form.querySelector('[name="id"]');
    var p = media.product;
    if (!input || !p || !p.variants) return null;
    for (var i = 0; i < p.variants.length; i++) if (String(p.variants[i].id) === String(input.value)) return p.variants[i];
    return null;
  }

  function syncOnLoad(media) {
    var v = currentVariant(media);
    if (!v || !v.featured_media) return;
    var want = colourValue(media, v), fid = String(v.featured_media.id);
    if (carouselVisible(media)) {
      var car = carousel(media);
      var target = car.querySelector('.product__media-item[data-media-id="' + fid + '"]');
      if (!target || target.classList.contains('is-filtered')) return;
      Promise.resolve(car.flickityInstance).then(function (fl) {
        if (!fl || typeof fl.selectCell !== 'function') return;
        if (showsColour(media, fl.selectedElement, v, want)) return;  // the slide actually on screen
        fl.selectCell('[data-media-id="' + fid + '"]', false, true); // Focal's own carousel, instant
        media.selectedVariantMediaId = v.featured_media.id;          // keep Focal's state in step
      }).catch(function () { /* never break the page */ });
    } else {
      if (showsColour(media, firstVisible(gridItems(media)), v, want)) return;
      refilterGrid(media, v);
    }
  }

  function boot() {
    var media = mainMedia(null);
    if (!media) return;
    var list = carousel(media);
    if (list && list.childElementCount <= 1) return; // single photo: nothing to sync (Focal skips it too)
    var tries = 0;
    (function wait() {
      var car = carousel(media);
      var ready = media.product && (!carouselVisible(media) || (car && car.flickityInstance));
      if (ready) { try { syncOnLoad(media); } catch (e) { /* never break the page */ } return; }
      if (++tries < 50) setTimeout(wait, 200);
    })();
  }

  function formIdOf(event) {
    // Focal dispatches on the <form>; its `.id` property is shadowed by <input name="id">, so read the attribute.
    var t = event && event.target;
    if (t && t.getAttribute && t.getAttribute('id')) return t.getAttribute('id');
    var f = event && event.detail && event.detail.formId;
    if (typeof f === 'string') return f;
    if (f && f.form && f.form.getAttribute) return f.form.getAttribute('id');
    return null;
  }

  document.addEventListener('variant:changed', function (event) {
    var d = event && event.detail;
    var v = d ? d.variant : null;
    if (!v) return;
    var fid = formIdOf(event);
    setTimeout(function () {
      var media = fid ? mainMedia(fid) : null;
      if (!media || carouselVisible(media)) return; // Rule 1: phones — Focal's own handler is correct.
      try { refilterGrid(media, v); } catch (e) { /* never break the page */ }
    }, 50);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
